import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { awardToken } from "../../../awardtoken.js";
import vocabData from "./ScienceVocabPool.js";
import "./SciencePractice.css";
import { db } from "../../../firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../../../components/useAuth";

const TOOL_TYPE = "ScienceVocabPractice";
const SOUND_SOURCES = [
  "/sounds/correct.mp3",
  "/sounds/correct.ogg",
  "/sounds/correct.wav",
];
const SHEET_SIZES = [10, 25, 50];
const SUBJECTS = ["biology", "physics", "chemistry"];
const GRADE_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);

async function logVocabPractice({
  user, grade, subject, correct, attempts, startTime, endTime
}) {
  if (!user?.uid) return;
  await setDoc(
    doc(db, "users", user.uid, "practiceRecords", TOOL_TYPE),
    {
      toolType: TOOL_TYPE,
      topic: grade <= 7 ? `Vocab G${grade}` : `Vocab G${grade} - ${subject}`,
      correct,
      total: attempts,   // total for dashboard
      attempts,
      accuracy: attempts > 0 ? Math.round((correct / attempts) * 100) : null, // <-- NEW
      timestamp: serverTimestamp(),
      startedAt: startTime,
      finishedAt: endTime,
      durationSec: Math.round((endTime - startTime) / 1000),
      grade,
      subject: grade >= 8 ? subject : "general",
    },
    { merge: true }
  );
}

function getRandomItems(array, n) {
  const arr = array.slice();
  const result = [];
  while (result.length < n && arr.length > 0) {
    const idx = Math.floor(Math.random() * arr.length);
    result.push(arr.splice(idx, 1)[0]);
  }
  return result;
}

// 工具：生成一道題（定義→四選一詞彙）
function generateQuestion(vocabList) {
  if (!vocabList || vocabList.length < 4) {
    return {
      definition: "Not enough vocabulary data.",
      answer: "",
      choices: [],
    };
  }
  const correct = vocabList[Math.floor(Math.random() * vocabList.length)];
  const distractors = getRandomItems(
    vocabList.filter((v) => v.term !== correct.term),
    3
  );
  const choices = getRandomItems([correct, ...distractors], 4);
  return {
    definition: correct.definition,
    answer: correct.term,
    choices: choices.map((v) => v.term),
  };
}

function PrintableSheet({ problems, showAnswers, onBack }) {
  return (
    <div className="worksheet">
      <h2 className="no-print">Vocabulary Worksheet</h2>
      <button className="no-print" onClick={onBack}>
        Back to Practice
      </button>
      <ol>
        {problems.map((q, idx) => (
          <li key={idx} style={{ marginBottom: "1em" }}>
            <div>
              <b>Definition:</b> {q.definition}
            </div>
            <div>
              <b>Choices:</b>{" "}
              {q.choices.map((c, ci) => (
                <span key={ci} style={{ marginRight: 10 }}>
                  {String.fromCharCode(65 + ci)}. {c}
                </span>
              ))}
            </div>
            {showAnswers && (
              <div style={{ color: "#0a0" }}>Answer: {q.answer}</div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function ScienceVocabularyPractice({ onAwardTokens } = {}) {
  // state for grade/subject
  const [grade, setGrade] = useState(1);
  const [subject, setSubject] = useState("biology");

  // 計算當前年級/主題的詞庫
  const vocabList = useMemo(() => {
    if (grade <= 7) {
      return vocabData[grade] || [];
    } else {
      return (vocabData[grade] && vocabData[grade][subject]) || [];
    }
  }, [grade, subject]);

  // core state
  const [currentQ, setCurrentQ] = useState(() => generateQuestion(vocabList));
  const [userChoice, setUserChoice] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [sheetProblems, setSheetProblems] = useState(null);
  const [showAnswers, setShowAnswers] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const tokensAwarded = useRef(0);
  const correctSoundRef = useRef(null);

  // Firestore logging
  const { user } = useAuth();
  const [sessionStart, setSessionStart] = useState(Date.now());

  // update subject if grade切換時自動設為biology
  useEffect(() => {
    if (grade <= 7) setSubject("biology");
  }, [grade]);

  // Award token every 10 correct
  useEffect(() => {
    if (correct >= (tokensAwarded.current + 1) * 10) {
      awardToken();
      tokensAwarded.current += 1;
      if (onAwardTokens) onAwardTokens(1);
    }
  }, [correct, onAwardTokens]);

  // 音效載入
  useEffect(() => {
    const audio = new Audio();
    for (const src of SOUND_SOURCES) {
      const type = src.endsWith(".mp3")
        ? "audio/mpeg"
        : src.endsWith(".ogg")
        ? "audio/ogg"
        : "audio/wav";
      if (audio.canPlayType(type)) {
        audio.src = src;
        break;
      }
    }
    correctSoundRef.current = audio;
  }, []);

  // 新題目
  const nextQuestion = useCallback(() => {
    setCurrentQ(generateQuestion(vocabList));
    setUserChoice(null);
    setFeedback(null);
  }, [vocabList]);

  // Log every answer
  useEffect(() => {
    if (attempts > 0) {
      const endTime = Date.now();
      logVocabPractice({
        user,
        grade,
        subject,
        correct,
        attempts,
        startTime: sessionStart,
        endTime,
      });
    }
    // eslint-disable-next-line
  }, [correct, attempts]);

  // Log & reset on grade/subject change
  useEffect(() => {
    if (attempts > 0) {
      const endTime = Date.now();
      logVocabPractice({
        user,
        grade,
        subject,
        correct,
        attempts,
        startTime: sessionStart,
        endTime,
      });
    }
    setSessionStart(Date.now());
    setAttempts(0);
    setCorrect(0);
    setUserChoice(null);
    setFeedback(null);
    setCurrentQ(generateQuestion(vocabList));
    tokensAwarded.current = 0;
    // eslint-disable-next-line
  }, [vocabList]);

  // Log on unmount
  useEffect(() => {
    return () => {
      if (attempts > 0) {
        const endTime = Date.now();
        logVocabPractice({
          user,
          grade,
          subject,
          correct,
          attempts,
          startTime: sessionStart,
          endTime,
        });
      }
    };
    // eslint-disable-next-line
  }, []);

  // 答案處理
  function handleChoice(choice) {
    setAttempts((a) => a + 1);
    setUserChoice(choice);
    if (choice === currentQ.answer) {
      setCorrect((c) => c + 1);
      setFeedback("✅ Correct!");
      if (correctSoundRef.current) {
        correctSoundRef.current.currentTime = 0;
        correctSoundRef.current.play();
      }
      setTimeout(nextQuestion, 700);
    } else {
      setFeedback(`❌ Incorrect. The answer is: ${currentQ.answer}`);
    }
  }

  // Sheet mode
  function startSheetMode(count) {
    const problemSet = Array.from({ length: count }, () =>
      generateQuestion(vocabList)
    );
    setSheetProblems(problemSet);
    setShowAnswers(false);
    setTimeout(() => window.print(), 100);
  }
  function leaveSheetMode() {
    setSheetProblems(null);
  }

  // printable mode
  if (sheetProblems) {
    return (
      <PrintableSheet
        problems={sheetProblems}
        showAnswers={showAnswers}
        onBack={leaveSheetMode}
      />
    );
  }

  // 練習模式UI
  return (
    <div className="practice-container">
      <h2>Science Vocabulary Practice</h2>
      {/* 年級選擇 */}
      <div style={{ margin: "1em 0" }}>
        <label>
          <b>Grade:</b>{" "}
          <select value={grade} onChange={(e) => setGrade(Number(e.target.value))}>
            {GRADE_OPTIONS.map((g) => (
              <option value={g} key={g}>
                Grade {g}
              </option>
            ))}
          </select>
        </label>
        {/* 8年級以上顯示主題選單 */}
        {grade >= 8 && (
          <label style={{ marginLeft: 16 }}>
            <b>Subject:</b>{" "}
            <select value={subject} onChange={(e) => setSubject(e.target.value)}>
              {SUBJECTS.map((s) => (
                <option value={s} key={s}>
                  {s[0].toUpperCase() + s.slice(1)}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div style={{ margin: "1.2em 0" }}>
        <b>Definition:</b> {currentQ.definition}
      </div>
      <div>
        {currentQ.choices.map((c, idx) => (
          <button
            key={idx}
            className={`vocab-choice-btn ${
              userChoice
                ? c === currentQ.answer
                  ? "correct"
                  : c === userChoice
                  ? "incorrect"
                  : ""
                : ""
            }`}
            disabled={userChoice}
            onClick={() => handleChoice(c)}
            style={{ marginRight: 14, marginBottom: 8 }}
          >
            {c}
          </button>
        ))}
      </div>
      {feedback && (
        <div style={{ margin: "1em 0", fontWeight: 500 }}>{feedback}</div>
      )}
      <p>
        Score: {correct} / {attempts}
      </p>
      <div className="worksheet-buttons">
        <strong>Printable worksheets:</strong>
        {SHEET_SIZES.map((cnt) => (
          <button
            key={cnt}
            onClick={() => startSheetMode(cnt)}
            style={{ marginLeft: "0.5rem" }}
          >
            {cnt} Questions
          </button>
        ))}
      </div>
    </div>
  );
}
